import { auth } from "@/lib/auth";
import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import type { UserInfo } from "../../../shared/user";
import { appApi } from "./appApi";

export type AuthState = {
  user: UserInfo | null;
};

// Inject auth endpoints into the main appApi
const userApi = appApi.injectEndpoints({
  endpoints: builder => ({
    getUserInfo: builder.query<{ user: UserInfo }, void>({
      query: () => "getUserInfo",
      transformErrorResponse: response => {
        if (response.status === "FETCH_ERROR") {
          return "Failed to fetch user info";
        }
        return response.data || "Failed to get user info";
      },
      providesTags: ["UserInfo"],
    }),
  }),
  overrideExisting: false,
});

const authApi = userApi.injectEndpoints({
  endpoints: builder => ({
    getSession: builder.query<AuthState, void>({
      queryFn: async (_, { dispatch }) => {
        const {
          data: { session },
          error,
        } = await auth.getSession();

        if (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error.message || "Failed to get session",
            },
          };
        }

        const userResult = await dispatch(
          userApi.endpoints.getUserInfo.initiate(undefined, { forceRefetch: true })
        );

        if (userResult.error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                ("message" in userResult.error && userResult.error.message) ||
                "Failed to get user info",
            },
          };
        }

        return { data: { user: userResult.data?.user ?? null } };
      },
      providesTags: ["Session"],
      onCacheEntryAdded: async (_arg, lifecycleApi) => {
        // don't listen for auth state changes until the cache is loaded
        // doing so would cause an initialization-time deadlock
        // because Supabase awaits all callbacks before resolving its `initializePromise`
        // and `auth.getSession()` awaits that and it's used by overriden `fetch` global
        await lifecycleApi.cacheDataLoaded;
        // Then listen for future auth state changes
        let currentSession: Session | null = null;
        const {
          data: { subscription },
        } = auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
          const previousSession = currentSession;
          currentSession = session;
          if (
            // we only want to listen for potential *changes* below so we can skip this
            event === "INITIAL_SESSION" ||
            // still no session so the anonymous user can still be "logged in"
            (!currentSession && !previousSession) ||
            // same user as before so the user doesn't have to be refetched
            (previousSession && currentSession?.user.id === previousSession.user.id)
          ) {
            return;
          }

          // Supabase's `_onVisibilityChanged` can call `_recoverAndRefresh` and in it we can find `await this._notifyAllSubscribers('SIGNED_IN', currentSession)`
          // so we absolutely can't await anything here as whatever we trigger might end up calling `supabase.auth.getSession()`
          // which would deadlock the entire app given it requires the same web lock as the one obtained by the mentioned `_onVisibilityChanged` caller
          lifecycleApi
            .dispatch(
              authApi.endpoints.getUserInfo.initiate(undefined, { forceRefetch: true })
            )
            .then(result => {
              lifecycleApi.updateCachedData(draft => {
                draft.user = result.data?.user ?? null;
              });
            });
        });

        await lifecycleApi.cacheEntryRemoved;
        subscription.unsubscribe();
      },
    }),
    signOut: builder.mutation<void, void>({
      queryFn: async () => {
        const { error } = await auth.signOut();

        if (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error.message || "Failed to sign out",
            },
          };
        }

        return { data: undefined };
      },
      invalidatesTags: ["Session"],
    }),
  }),
  overrideExisting: false,
});

// Export the generated hooks
export const { useGetSessionQuery, useSignOutMutation } = authApi;
